import * as socketClusterClient from 'socketcluster-client'
import {redactSensitive, safeStringify} from '~/utils/sanitize.js'

class SocketService {
    constructor() {
        this.socket = null
        this.socketStore = useSocketStore()
        this.userStore = useUserStore()
        this.isInitialized = false
        this.tokenRefreshTimer = null
        this.authRetryCount = new Map()
        this.MAX_AUTH_RETRIES = 3
        this.healthCheckInterval = null
        this.lastPongTime = Date.now()
        this.HEALTH_CHECK_INTERVAL = 30000
        this.STALE_THRESHOLD = 60000
        this.reconnectAttempts = 0
        this.MAX_RECONNECT_ATTEMPTS = 10
        this.lastConnectTime = 0
        this.MIN_CONNECT_INTERVAL = 5000
    }

    // Initialize and connect the socket
    async connect(config) {
        if (!this.config)
            this.config = config

        // Disconnect existing connection if any. Crucially, killAllListeners()
        // BEFORE clearing the reference — without that, the four
        // `for await (... of socket.listener(...))` loops started in the
        // previous setupEventListeners() keep iterating against the dead
        // socket reference forever, holding closure references to userStore
        // / socketStore and never being GC'd. After enough reconnect cycles
        // (POS networks flap frequently) this is the dominant memory leak.
        if (this.socket) {
            try { this.socket.killAllListeners() } catch (e) {}
            this.socket.disconnect()
            this.socket = null
        }

        // Reset store state
        this.socketStore.isConnected = false
        this.socketStore.isConnecting = false
        this.socketStore.setAuthenticated(false)

        console.log('Auth check:', {
            isAuthenticated: this.userStore.isAuthenticated,
            hasProfile: !!this.userStore.profile,
            hasWsToken: !!this.userStore.wsToken,
            wsToken: this.userStore.wsToken?.substring(0, 20) + '...', // Don't log full token
            businessId: this.userStore.activeBusinessId,
            isTokenExpired: this.userStore.isWsTokenExpired,
        })

        // Validate prerequisites
        if (!this.userStore.isAuthenticated || !this.userStore.wsToken || !this.userStore.activeBusinessId) {
            const error = "Cannot connect: Missing auth token or business ID."

            this.socketStore.setError(error)
            throw new Error(error)
        }

        // Check if token is expired BEFORE attempting connection
        if (this.userStore.isWsTokenExpired) {
            console.log('SC: WS token is expired, refreshing before connection...')
            try {
                await this.refreshToken()
                console.log('SC: Token refreshed successfully before connection')
            } catch (refreshError) {
                console.error('SC: Failed to refresh token before connection:', refreshError)
                this.socketStore.setError(`Token refresh failed: ${refreshError.message}`)
                throw refreshError
            }
        }

        // Prevent multiple connections
        if (this.socketStore.isConnecting) {
            console.warn("SC: Socket connection attempt already in progress.")

            return
        }

        this.socketStore.setConnecting()

        // Build and store the actual server URL we're connecting to
        const protocol = config.secure ? 'wss' : 'ws'
        const serverUrl = `${protocol}://${config.hostname}:${config.port}`
        this.socketStore.serverUrl = serverUrl

        console.log(`SC: Connecting to ${serverUrl}...`)

        try {
            console.log('[SocketService] Creating socket with config:', config)
            this.socket = socketClusterClient.create({
                hostname: config.hostname,
                port: config.port,
                secure: config.secure,
                reconnect: config.reconnect !== false, // Default to true
            })

            console.log('[SocketService] Socket created:', !!this.socket)
            console.log('[SocketService] Socket initial state:', this.socket?.state)

            // Set up event listeners immediately after creating socket
            this.setupEventListeners()

        } catch (error) {
            this.socketStore.isConnecting = false
            this.socketStore.setError(`Failed to create socket: ${error.message}`)
            throw error
        }
    }

    // Set up all socket event listeners
    setupEventListeners() {
        if (!this.socket) return
                // Handle connection success
                ;
        (async () => {
            for await (let event of this.socket.listener('connect')) {
                const now = Date.now()
                const timeSinceLastConnect = now - this.lastConnectTime

                if (timeSinceLastConnect < this.MIN_CONNECT_INTERVAL) {
                    console.log(`SC: Ignoring rapid reconnect (${timeSinceLastConnect}ms since last connect)`)
                    continue
                }

                this.lastConnectTime = now
                console.log('SC Connected:', {id: this.socket.id, state: this.socket.state})
                this.socketStore.socketConnectionStatus = `Connected`
                this.socketStore.socketConnectionColor = 'success'
                this.socketStore.socketId = this.socket.id

                // Authenticate after connection
                try {
                    await this.authenticate()
                } catch (authError) {
                    console.error('SC Auth Error:', authError)
                    this.socketStore.socketConnectionStatus = `Auth Failed: ${authError.message}`
                    this.socketStore.socketConnectionColor = 'error'
                    this.socket.disconnect()
                }
            }
        })()/*().catch(connectError => {
      this.socketStore.isConnecting = false
      this.socketStore.setError(`Connection Failed: ${connectError.message}`)
      console.error('SC Connection Error:', connectError)
    })*/

        // Handle connection errors
        ;(async () => {
            for await (let error of this.socket.listener('error')) {
                this.socketStore.isConnecting = false
                const errorMsg = error?.message || error?.toString() || 'Unknown socket error'
                this.socketStore.setError(`Socket Error: ${errorMsg.substring(0, 100)}`)
                console.error('SC error:', error ?? 'Server error')
            }
        })()

        // Handle disconnection
        ;(async () => {
            for await (let disconnectData of this.socket.listener('disconnect')) {
                this.socketStore.isConnecting = false
                this.socketStore.socketConnectionStatus = `Disconnected`
                this.socketStore.socketConnectionColor = (disconnectData.code === 1000 ? 'info' : 'warning')
                console.log('SC closed:', disconnectData.code, disconnectData.reason)
                this.socketStore.setDisconnected(disconnectData.code, disconnectData.reason)

                // Don't manually reconnect - let socketcluster-client handle reconnection
                // Manual reconnection causes infinite loops on Android
                // The socket has reconnect: true by default which handles this
            }
        })()

        // Handle authentication state changes
        ;(async () => {
            for await (let authState of this.socket.listener('authStateChange')) {
                console.log('[SocketCluster] Auth State Change:', {
                    hasAuthToken: !!authState.authToken,
                    accountId: authState.authToken?.accountId,
                    oldAuthState: authState.oldAuthState
                })
                if (authState.authToken && authState.authToken.accountId) {
                    this.socketStore.setAuthenticated(true)
                    this.socketStore.isConnecting = false
                    this.socketStore.isConnected = true
                    this.socketStore.socketConnectionStatus = 'Stable'
                    this.socketStore.socketConnectionColor = 'success'

                    console.log('[SocketCluster] ✅ Authentication successful - socket is now fully ready')
                    console.log('[SocketCluster] Socket State:', {
                        isConnected: this.socketStore.isConnected,
                        isAuthenticated: this.socketStore.isAuthenticated,
                        socketId: this.socket.id
                    })
                } else {
                    this.socketStore.setAuthenticated(false)
                    console.log('[SocketCluster] ❌ Authentication lost')
                }
            }
        })()
    }

    // Authenticate the connection
    async authenticate() {
        if (!this.socket || this.socket.state !== 'open') {
            throw new Error("Cannot authenticate, socket not open.")
        }

        if (!this.userStore.isAuthenticated || !this.userStore.wsToken || !this.userStore.activeBusinessId) {
            throw new Error("Cannot authenticate: Missing auth token or business ID.")
        }

        // Proactively check if token is expired before authentication
        if (this.userStore.isWsTokenExpired) {
            console.log('SC: Token is expired before auth, refreshing...')
            try {
                await this.refreshToken()
                console.log('SC: Token refreshed successfully before auth')
            } catch (refreshError) {
                console.error('SC: Failed to refresh token before auth:', refreshError)
                throw refreshError
            }
        }

        try {
            console.log('SC: Attempting authentication...')

            const authResult = await this.socket.invoke('login', {
                token: this.userStore.wsToken,
                businessIdForChannel: this.userStore.activeBusinessId,
            }, {
                timeout: 10000, // 10 seconds instead of default 5
            })

            this.socketStore.socketConnectionStatus = 'Stable'
            this.socketStore.isConnected = true
            this.socketStore.isConnecting = false
            this.socketStore.isAuthenticated = true
            console.log('SC: Authentication result:', redactSensitive(authResult))
            console.log('SC: Authenticated for Business ID:', this.userStore.activeBusinessId)

            this.scheduleTokenRefresh()
            this.startHealthCheck()
            this.reconnectAttempts = 0

            return authResult
        } catch (err) {
            console.error('SC: Authentication failed:', err)
            this.socketStore.setAuthenticated(false, err.message)

            // If auth fails due to expired token, try to refresh it
            if (err.name === 'TokenExpiredError' || err.message?.includes('expired') || err.message?.includes('jwt expired')) {
                console.log("SC: Token expired, attempting refresh...")
                try {
                    await this.refreshToken()
                    console.log('SC: Token refreshed successfully, retrying authentication')
                    return await this.authenticate()
                } catch (refreshError) {
                    console.error('SC: Token refresh failed, disconnecting socket:', refreshError)
                    this.disconnect()
                    throw refreshError
                }
            }

            throw err
        }
    }

    // Subscribe to a channel and attach data handler
    async subscribe(channelName, dataHandler, fetchPendingOrders) {
        if (!this.socket || this.socket.state !== 'open') {
            const error = "Cannot subscribe: Socket not connected"
            console.warn('[SocketCluster] Subscribe failed:', {
                hasSocket: !!this.socket,
                socketState: this.socket?.state,
                storeConnected: this.socketStore.isConnected,
                error
            })
            return {error, businessChannel: null}
        }

        if (!this.userStore.activeBusinessId || !this.socket.authToken) {
            const error = "Cannot subscribe: Not authenticated"
            console.warn('[SocketCluster] Subscribe failed:', {
                hasBusinessId: !!this.userStore.activeBusinessId,
                hasAuthToken: !!this.socket.authToken,
                error
            })
            return {error, businessChannel: null}
        }

        try {
            const businessChannel = this.socket.subscribe(channelName)

            console.log('businessChannel:', businessChannel)

            // Handle subscription success
            ;(async () => {
                for await (let subscribeData of businessChannel.listener('subscribe')) {
                    this.socketStore.socketConnectionStatus = `Stable`
                    this.socketStore.socketConnectionColor = 'success'
                    console.log(`[SocketCluster] ✅ Successfully subscribed to ${channelName}`)
                    console.log(`[SocketCluster] Subscription data:`, subscribeData)
                    console.log(`[SocketCluster] Socket state:`, this.socket.state)
                    console.log(`[SocketCluster] Is authenticated:`, this.socketStore.isAuthenticated)

                    // Reset retry counter on successful subscription
                    this.authRetryCount.delete(channelName)

                    if (typeof fetchPendingOrders === 'function') {
                        try {
                            await fetchPendingOrders()
                        } catch (fetchError) {
                            console.error('Error fetching pending orders:', fetchError)
                        }
                    }
                }
            })()

            // Handle subscription failure
            ;(async () => {
                for await (let error of businessChannel.listener('subscribeFail')) {
                    this.socketStore.socketConnectionStatus = `Subscription Failed: ${error.message || error}`
                    this.socketStore.socketConnectionColor = 'error'
                    console.error(`SC subscribeFail to ${channelName}:`, error)

                    // Check if it's an authentication error
                    const errorMsg = error?.message || error?.toString() || ''
                    const isAuthError = errorMsg.includes('Not authenticated') || errorMsg.includes('authentication') || errorMsg.includes('Unauthorized')

                    if (isAuthError) {
                        const currentRetries = this.authRetryCount.get(channelName) || 0

                        if (currentRetries < this.MAX_AUTH_RETRIES) {
                            const newRetryCount = currentRetries + 1
                            this.authRetryCount.set(channelName, newRetryCount)
                            const delay = Math.min(1000 * Math.pow(2, currentRetries), 5000) // Exponential backoff: 1s, 2s, 4s

                            console.warn(`[Socket] Authentication error - retrying in ${delay}ms (attempt ${newRetryCount}/${this.MAX_AUTH_RETRIES})`)

                            // Tear down the failed channel BEFORE retrying.
                            // Without this, each retry adds another set of
                            // for-await loops on top of the dead channel —
                            // up to 4 zombie subscriptions per channel under
                            // flapping auth, each holding closure refs to
                            // userStore and never GC'd. killAllListeners()
                            // terminates the loops attached above; channel
                            // unsubscribe drops the SC-side subscription so
                            // the next subscribe() actually opens fresh.
                            try {
                                businessChannel.killAllListeners()
                                businessChannel.unsubscribe()
                            } catch (cleanupErr) {
                                console.warn('[Socket] Pre-retry cleanup error (non-fatal):', cleanupErr)
                            }

                            setTimeout(async () => {
                                console.log(`[Socket] Retry attempt ${newRetryCount} for ${channelName}`)
                                await this.subscribe(channelName, dataHandler, fetchPendingOrders)
                            }, delay)
                            return // Stop the outer for-await — we just terminated the channel
                        } else {
                            console.error('[Socket] Max auth retries reached - clearing session and redirecting to login')
                            this.authRetryCount.delete(channelName)
                            try {
                                businessChannel.killAllListeners()
                                businessChannel.unsubscribe()
                            } catch (cleanupErr) {}
                            this.userStore.logout()

                            // Navigate to login using Nuxt's navigateTo
                            const nuxtApp = useNuxtApp()
                            if (nuxtApp?.$router) {
                                await nuxtApp.$router.push('/auth/login')
                            }
                            return
                        }
                    }
                }
            })()

            // Handle incoming messages from the channel
            ;(async () => {
                console.log(`[SocketCluster] 👂 Listening for messages on ${channelName}...`)
                for await (let data of businessChannel) {
                    this.updateLastPong()

                    console.log(`[SocketCluster] 📨 Raw message received on ${channelName}:`)
                    console.log(`[SocketCluster] Message structure:`, safeStringify(data))
                    console.log(`[SocketCluster] Event:`, data?.event)
                    console.log(`[SocketCluster] Payload:`, redactSensitive(data?.payload))

                    if (data?.event && data?.payload) {
                        console.log(`[SocketCluster] ✅ Valid message format - calling dataHandler`)
                        try {
                            dataHandler(data?.event, data?.payload)
                            console.log(`[SocketCluster] ✅ dataHandler executed successfully`)
                        } catch (handlerError) {
                            console.error('[SocketCluster] ❌ Error in data handler:', handlerError)
                            console.error('[SocketCluster] Handler error stack:', handlerError.stack)
                        }
                    } else {
                        console.warn(`[SocketCluster] ⚠️ Invalid message format - missing event or payload`)
                    }
                }
            })()

            return {error: null, businessChannel}
        } catch (error) {
            console.error('SC: Subscription failed:', error)

            return {error: error.message, businessChannel: null}
        }
    }

    // Check if socket is ready for operations
    isReady() {
        const ready = this.socket &&
                this.socket.state === 'open' &&
                this.socketStore.isConnected &&
                this.socketStore.isAuthenticated

        console.log('[SocketService] isReady() check:', {
            hasSocket: !!this.socket,
            socketState: this.socket?.state,
            storeConnected: this.socketStore.isConnected,
            storeAuthenticated: this.socketStore.isAuthenticated,
            result: ready
        })

        return ready
    }

    // Get current connection status
    getStatus() {
        return {
            isConnected: this.socketStore.isConnected,
            isConnecting: this.socketStore.isConnecting,
            isAuthenticated: this.socketStore.isAuthenticated,
            socketId: this.socketStore.socketId,
            status: this.socketStore.socketConnectionStatus,
            color: this.socketStore.socketConnectionColor,
        }
    }

    // Disconnect from the server
    disconnect() {
        if (this.tokenRefreshTimer) {
            clearTimeout(this.tokenRefreshTimer)
            this.tokenRefreshTimer = null
        }
        this.stopHealthCheck()
        if (this.socket) {
            // Terminate the for-await loops attached by setupEventListeners
            // (and any per-channel subscribe loops) before tearing down the
            // socket. Without this, the loops continue iterating after
            // disconnect, holding store references and preventing GC.
            try { this.socket.killAllListeners() } catch (e) {}
            this.socket.disconnect()
        }

        // Reset store state
        this.socketStore.isConnected = false
        this.socketStore.isConnecting = false
        this.socketStore.setAuthenticated(false)
        this.socketStore.clearAllSubscriptions()
        this.socketStore.socketConnectionStatus = 'Disconnected'
        this.socketStore.socketConnectionColor = 'info'

        console.log("SC: Socket disconnected.")
    }

    startHealthCheck() {
        this.stopHealthCheck()
        this.lastPongTime = Date.now()

        this.healthCheckInterval = setInterval(async () => {
            await this.performHealthCheck()
        }, this.HEALTH_CHECK_INTERVAL)

        console.log('[SC] Health check started - interval:', this.HEALTH_CHECK_INTERVAL, 'ms')
    }

    stopHealthCheck() {
        if (this.healthCheckInterval) {
            clearInterval(this.healthCheckInterval)
            this.healthCheckInterval = null
        }
    }

    async performHealthCheck() {
        if (!this.socket) {
            console.log('[SC] Health check: No socket instance')
            return
        }

        const socketState = this.socket.state
        const timeSinceLastPong = Date.now() - this.lastPongTime

        console.log('[SC] Health check:', {
            state: socketState,
            timeSinceLastPong: Math.round(timeSinceLastPong / 1000) + 's',
            isConnected: this.socketStore.isConnected,
            isAuthenticated: this.socketStore.isAuthenticated
        })

        if (socketState !== 'open') {
            console.log('[SC] Health check: Socket not open, triggering reconnect')
            await this.handleStaleConnection('socket_not_open')
            return
        }

        if (timeSinceLastPong > this.STALE_THRESHOLD) {
            console.log('[SC] Health check: Connection appears stale, testing...')

            try {
                const pongReceived = await this.pingServer()
                if (pongReceived) {
                    this.lastPongTime = Date.now()
                    this.reconnectAttempts = 0
                    console.log('[SC] Health check: Ping successful, connection is healthy')
                } else {
                    console.log('[SC] Health check: Ping failed, connection is stale')
                    await this.handleStaleConnection('ping_failed')
                }
            } catch (err) {
                console.error('[SC] Health check: Ping error:', err)
                await this.handleStaleConnection('ping_error')
            }
        } else {
            this.reconnectAttempts = 0
        }
    }

    async pingServer() {
        if (!this.socket || this.socket.state !== 'open') {
            return false
        }

        try {
            const result = await Promise.race([
                this.socket.invoke('ping', {timestamp: Date.now()}),
                new Promise((_, reject) =>
                    setTimeout(() => reject(new Error('Ping timeout')), 10000)
                )
            ])
            return true
        } catch (err) {
            console.warn('[SC] Ping failed:', err.message)
            return false
        }
    }

    async handleStaleConnection(reason) {
        console.log('[SC] Handling stale connection:', reason)

        if (this.reconnectAttempts >= this.MAX_RECONNECT_ATTEMPTS) {
            console.error('[SC] Max reconnect attempts reached, giving up')
            this.socketStore.setError('Connection lost - max reconnect attempts reached')
            return
        }

        this.reconnectAttempts++
        const backoffDelay = Math.min(1000 * Math.pow(2, this.reconnectAttempts - 1), 30000)

        console.log(`[SC] Reconnect attempt ${this.reconnectAttempts}/${this.MAX_RECONNECT_ATTEMPTS} in ${backoffDelay}ms`)

        this.socketStore.socketConnectionStatus = `Reconnecting (${this.reconnectAttempts}/${this.MAX_RECONNECT_ATTEMPTS})...`
        this.socketStore.socketConnectionColor = 'warning'

        await new Promise(resolve => setTimeout(resolve, backoffDelay))

        if (this.config) {
            try {
                await this.connect(this.config)
                console.log('[SC] Reconnection successful')
            } catch (err) {
                console.error('[SC] Reconnection failed:', err)
            }
        }
    }

    async handleSystemWake(wakeInfo) {
        console.log('[SC] System wake detected:', wakeInfo)

        this.lastPongTime = Date.now() - this.STALE_THRESHOLD - 1000

        setTimeout(async () => {
            await this.performHealthCheck()
        }, 2000)
    }

    updateLastPong() {
        this.lastPongTime = Date.now()
    }

    // New method to schedule the refresh
    scheduleTokenRefresh() {
        if (this.tokenRefreshTimer) {
            clearTimeout(this.tokenRefreshTimer) // Clear any existing timer
        }
        const token = this.userStore.wsToken
        if (!token) return

        try {
            // Decode the JWT to get its expiry time ('exp' claim)
            const payloadBase64 = token.split('.')[1]
            const decodedPayload = JSON.parse(atob(payloadBase64))
            const expiryTimestamp = decodedPayload.exp * 1000 // exp is in seconds, convert to ms
            const now = Date.now()
            const timeUntilExpiry = expiryTimestamp - now

            // Schedule refresh 1-2 minutes before actual expiry
            const refreshBuffer = 5 * 60 * 1000 // 2 minutes in ms
            const refreshIn = timeUntilExpiry - refreshBuffer

            if (refreshIn > 0) {
                console.log(`SC: Scheduling token refresh in ${(refreshIn / 1000 / 60).toFixed(1)} minutes.`)
                this.tokenRefreshTimer = setTimeout(async () => {
                    await this.refreshTokenAndReauthenticate()
                }, refreshIn)
            } else {
                console.warn("SC: Token will expire very soon or has already expired. Refreshing now.")
                this.refreshTokenAndReauthenticate().then(r => {
                    console.log('Token refreshed')
                })
            }
        } catch (e) {
            console.error("SC: Could not decode JWT to schedule refresh.", e)

            // Maybe try to refresh after a fixed interval as a fallback
        }
    }

    // Refresh token only (no re-authentication)
    async refreshToken() {
        console.log("SC: Attempting to refresh WebSocket token...")
        try {
            // Use useApi to call your AdonisJS refresh endpoint
            // This call MUST be authenticated with the user's HTTP token
            const responseData = await useApi(
                    '/api/refresh-ws-token',
                    {},
                    {method: 'POST'},
                    2, // $fetch behavior
            )

            if (responseData && responseData.wsToken) {
                console.log("SC: New wsToken received.")

                // Update the Pinia store with the new token
                this.userStore.setWsToken(responseData.wsToken)
            } else {
                throw new Error("Invalid response from token refresh endpoint.")
            }
        } catch (error) {
            console.error("SC: Failed to refresh WebSocket token:", error)
            this.socketStore.setError("Session refresh failed. Connection may be lost.")
            throw error
        }
    }

    // Refresh token AND re-authenticate (for scheduled refresh)
    async refreshTokenAndReauthenticate() {
        await this.refreshToken()

        // Only re-authenticate if socket is connected and open
        if (this.socket && this.socket.state === 'open') {
            await this.authenticate()
        } else {
            console.log('SC: Socket not open, skipping immediate re-authentication')
        }
    }
}

export default defineNuxtPlugin(nuxtApp => {
    const socketService = new SocketService()

    // Make the service available globally as $sc
    nuxtApp.provide('sc', socketService)
})
